/**
 * Deterministic, offline Harvard reference-list linter.
 *
 * Pure string-in → findings-out, mirroring the shape of the MLA and Chicago linters
 * (`src/lib/citation-check-mla.ts`, `src/lib/citation-check-chicago.ts`). This module is
 * intentionally SELF-CONTAINED: it does NOT import or touch the citation-formatting core
 * (`src/lib/citation.ts`, `src/lib/bibliography.ts`) or any CSL fixture, so it can never
 * regress a golden snapshot. Every finding is *advisory* — a "likely issue to review",
 * never a claim that a reference is correct. The checks are deliberately high-precision
 * (a false positive is worse than a miss).
 *
 * IMPORTANT: "Harvard" is not one standard — universities publish many institutional
 * variants. This linter targets the widely-taught Cite Them Right conventions: authors as
 * "Last, F.M." with initials (not full first names), the year in parentheses directly
 * after the authors — "(2020)" — the word "and" before the final author, sentence case
 * for article titles, "Available at:" plus "[Accessed date]" for URLs, a terminal period,
 * A–Z ordering, and no duplicate entries. Always defer to your institution's own guide.
 */

export type Confidence = "high" | "medium";

export interface Finding {
  /** Stable rule id (used as React key / for tests). */
  rule: string;
  /** Short human-readable label. */
  title: string;
  /** Explanation of what looks off and why. */
  message: string;
  /** Optional copy-to-clipboard suggested fix. */
  suggestion?: string;
  confidence: Confidence;
  /** "Why" link — an authoritative Harvard guideline or one of our own tools. */
  learnMoreUrl: string;
  learnMoreLabel: string;
}

export interface EntryReport {
  /** 1-based position in the pasted list. */
  position: number;
  /** The normalized single-line entry text that was checked. */
  text: string;
  findings: Finding[];
}

export interface CheckReport {
  entries: EntryReport[];
  /** List-level findings (ordering, connector consistency, duplicates). */
  listFindings: Finding[];
  totalEntries: number;
  totalFindings: number;
}

// --- Module-level regexes (Biome: regex must not live inside loops) ------------

const RE_BLANK_LINE = /\n[ \t]*\n/;
const RE_BLANK_LINE_SPLIT = /\n[ \t]*\n/;
const RE_NEWLINE = /\n/;
const RE_SENTENCE_BREAK = /\.\s/;
const RE_LEADING_ARTICLE = /^(?:a|an|the)\s+/i;
const RE_QUOTED_TITLE = /['‘’"“”]([^'‘’"“”]{4,})['‘’"“”]/;
const RE_PAREN_YEAR = /\((?:(?:19|20)\d{2}[a-z]?|no date|n\.d\.)\)/i;
const RE_YEAR_ANYWHERE = /\b((?:19|20)\d{2})\b/;
const RE_FULL_GIVEN_NAME = /^([A-Z][A-Za-z'-]+),\s+([A-Z][a-z]{2,})\b/;
const RE_GIVEN_NAME_WORD = /^[A-Z][a-z]{2,}$/;
const RE_URL = /https?:\/\/\S+/i;
const RE_AVAILABLE_AT = /\bavailable\s+(?:at|from)\b:?/i;
// Both access-date forms: Cite Them Right's "(Accessed: 4 May 2026)" and the
// square-bracket "[Accessed 4 May 2026]" many university guides use.
const RE_ACCESSED_BRACKET = /\[accessed[^\]]*\]|\(accessed[^)]*\)/i;
const RE_ENDS_WITH_URL = /(https?:\/\/\S+|\b10\.\d{4,}\/\S+)$/;
const RE_TERMINAL_PUNCT = /[.?!]$/;
const RE_AMPERSAND = /\s&\s/;
const RE_AND_CONNECTOR = /\band\b/;
const RE_TOKEN_SPLIT = /\s+/;
const RE_NON_LETTER = /[^a-z]/g;
const RE_NON_LETTER_KEEP_CASE = /[^a-zA-Z]/g;
const RE_WORD_SPLIT = /\s+/;
const RE_PUNCT_STRIP = /[^a-z0-9]+/g;
const RE_LEADING_UPPER = /^[A-Z]/;
const RE_TRAILING_COMMA = /,$/;
const RE_PIPES = /\|/g;

/**
 * Function words that stay lowercase even in a Title-Case title. The sentence-case
 * heuristic ignores these so it only judges genuine *principal* words — a sentence-case
 * title is not penalized for a capitalized "The" mid-sentence, and a Title-Case title is
 * still detected even though "of" and "the" are lowercase in it.
 */
const FUNCTION_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "but",
  "for",
  "nor",
  "or",
  "so",
  "yet",
  "as",
  "at",
  "by",
  "in",
  "of",
  "off",
  "on",
  "per",
  "to",
  "up",
  "via",
  "with",
  "from",
  "into",
  "onto",
  "over",
  "than",
  "upon",
  "near",
  "amid",
  "among",
  "about",
  "above",
  "after",
  "along",
  "below",
  "beside",
  "between",
  "beyond",
  "during",
  "except",
  "inside",
  "toward",
  "towards",
  "under",
  "until",
  "within",
  "without",
  "across",
  "against",
  "before",
  "behind",
  "beneath",
  "outside",
  "through",
  "throughout",
]);

// Harvard (Cite Them Right) guideline references reused across findings.
const CTR_URL = "https://www.citethemrightonline.com/";
const CTR_LABEL = "Cite Them Right (Harvard)";

/**
 * Split pasted text into individual reference entries. Blank-line-separated blocks are
 * treated as one entry each (wrapped/hanging-indent lines are rejoined with a space);
 * otherwise each non-empty line is its own entry.
 */
export function splitEntries(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  if (RE_BLANK_LINE.test(trimmed)) {
    return trimmed
      .split(RE_BLANK_LINE_SPLIT)
      .map((block) =>
        block
          .split(RE_NEWLINE)
          .map((line) => line.trim())
          .join(" ")
          .trim()
      )
      .filter(Boolean);
  }
  return trimmed
    .split(RE_NEWLINE)
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * The author segment is everything before the parenthesized year — the Harvard delimiter.
 * When no "(year)" exists we fall back to the first ". " boundary, mirroring the MLA
 * checker, so the author rules still run on entries that are missing the parentheses.
 */
function authorSegment(entry: string): string | null {
  const parenIdx = entry.indexOf("(");
  if (parenIdx > 0) {
    return entry.slice(0, parenIdx).trim().replace(RE_TRAILING_COMMA, "");
  }
  const idx = entry.search(RE_SENTENCE_BREAK);
  if (idx <= 0) {
    return null;
  }
  return entry.slice(0, idx).trim().replace(RE_TRAILING_COMMA, "");
}

/**
 * The title segment is the quoted source title if present (article/chapter — Harvard uses
 * single quotes), otherwise the first sentence-delimited element after the "(year)".
 */
function titleSegment(entry: string): string | null {
  const quoted = RE_QUOTED_TITLE.exec(entry);
  if (quoted) {
    return quoted[1].trim();
  }
  const parenEnd = entry.indexOf(")");
  if (parenEnd < 0) {
    return null;
  }
  const rest = entry.slice(parenEnd + 1).trim();
  const next = rest.search(RE_SENTENCE_BREAK);
  if (next < 0) {
    return rest.replace(RE_TERMINAL_PUNCT, "").trim();
  }
  return rest.slice(0, next).trim();
}

/** Convert given-name words to Harvard initials: "John Maynard" → "J.M." */
function toInitials(givenNames: string): string {
  return givenNames
    .trim()
    .split(RE_TOKEN_SPLIT)
    .filter((w) => RE_NON_LETTER_KEEP_CASE.test(w) || w.length > 0)
    .map((w) => `${w.charAt(0).toUpperCase()}.`)
    .join("");
}

/**
 * Rule 1 — author written as "Last, First" with a FULL given name where Harvard
 * (Cite Them Right) wants initials: "Smith, J." not "Smith, John". High-precision: only
 * fires when the word after the comma is a capitalized word of 3+ letters (an initial
 * like "J." or "J.M." never matches).
 */
function checkAuthorInitials(authorSeg: string): Finding | null {
  const match = RE_FULL_GIVEN_NAME.exec(authorSeg);
  if (!match) {
    return null;
  }
  const [, surname] = match;
  // Collect the full run of given-name words after the comma for the suggestion.
  const afterComma = authorSeg.slice(authorSeg.indexOf(",") + 1).trim();
  const givenWords: string[] = [];
  for (const word of afterComma.split(RE_TOKEN_SPLIT)) {
    if (RE_GIVEN_NAME_WORD.test(word)) {
      givenWords.push(word);
    } else {
      break;
    }
  }
  const suggestion =
    givenWords.length > 0
      ? `${surname}, ${toInitials(givenWords.join(" "))}`
      : undefined;
  return {
    rule: "author-initials",
    title: "Author may use a full first name instead of initials",
    message:
      'Harvard (Cite Them Right) abbreviates given names to initials — "Smith, J." or "Smith, J.M.", not "Smith, John". This entry appears to spell out a full first name.',
    suggestion,
    confidence: suggestion ? "high" : "medium",
    learnMoreUrl: CTR_URL,
    learnMoreLabel: CTR_LABEL,
  };
}

/**
 * Rule 2 — the year should sit in parentheses directly after the authors: "(2020)".
 * Fires when a plausible year appears in the entry but no "(year)" (or "(no date)")
 * element exists anywhere.
 */
function checkYearInParens(entry: string): Finding | null {
  if (RE_PAREN_YEAR.test(entry)) {
    return null;
  }
  const year = RE_YEAR_ANYWHERE.exec(entry);
  if (!year) {
    return null;
  }
  return {
    rule: "year-in-parentheses",
    title: "Year is not in parentheses after the authors",
    message: `Harvard places the year of publication in parentheses immediately after the author element — e.g. "Smith, J. (${year[1]}) Title…". This entry contains "${year[1]}" but no parenthesized year. If the source is undated, use "(no date)".`,
    suggestion: `(${year[1]})`,
    confidence: "high",
    learnMoreUrl: CTR_URL,
    learnMoreLabel: CTR_LABEL,
  };
}

/** Principal (4+ letter, non-function) words after the first word of a title. */
function principalWords(title: string): string[] {
  return title
    .trim()
    .split(RE_WORD_SPLIT)
    .slice(1) // the first word is capitalized in both title and sentence case
    .filter((w) => w.replace(RE_NON_LETTER_KEEP_CASE, "").length >= 4)
    .filter((w) => !FUNCTION_WORDS.has(w.toLowerCase()));
}

/**
 * Rule 3 — an article/chapter title typed in Title Case where Harvard wants sentence
 * case (the reverse of the MLA rule). High-precision: only fires on a *quoted* title in
 * which EVERY principal word is capitalized — proper nouns inside a sentence-case title
 * will not trip this on their own.
 */
function checkSentenceCase(entry: string): Finding | null {
  const quoted = RE_QUOTED_TITLE.exec(entry);
  if (!quoted) {
    return null;
  }
  const words = principalWords(quoted[1].trim());
  if (words.length < 3) {
    return null;
  }
  const allCapitalized = words.every((w) => RE_LEADING_UPPER.test(w));
  if (!allCapitalized) {
    return null;
  }
  return {
    rule: "sentence-case-title",
    title: "Article title may be in Title Case",
    message:
      "Harvard (Cite Them Right) uses sentence case for article and chapter titles — capitalize only the first word and proper nouns, e.g. ‘The effects of sleep on memory’. Every principal word in this quoted title is capitalized, which looks like Title Case.",
    confidence: "medium",
    learnMoreUrl: CTR_URL,
    learnMoreLabel: CTR_LABEL,
  };
}

/**
 * Rule 4 — an online source should pair "Available at: <URL>" with an access date,
 * written "(Accessed: 4 May 2026)" (Cite Them Right) or "[Accessed 4 May 2026]".
 * Fires when a URL is present but either half of the pairing is missing.
 */
function checkAvailableAccessed(entry: string): Finding | null {
  if (!RE_URL.test(entry)) {
    return null;
  }
  const hasAvailable = RE_AVAILABLE_AT.test(entry);
  const hasAccessed = RE_ACCESSED_BRACKET.test(entry);
  if (hasAvailable && hasAccessed) {
    return null;
  }
  const missing: string[] = [];
  if (!hasAvailable) {
    missing.push('"Available at:" before the URL');
  }
  if (!hasAccessed) {
    missing.push('an "(Accessed: date)" after it');
  }
  return {
    rule: "available-accessed-pairing",
    title: 'URL is missing the "Available at:" / "[Accessed date]" pairing',
    message: `Harvard (Cite Them Right) cites online sources as "Available at: <URL> (Accessed: 4 May 2026)." (many university guides use square brackets: "[Accessed 4 May 2026]"). This entry has a URL but is missing ${missing.join(" and ")}.`,
    suggestion: "Available at: <URL> (Accessed: 4 May 2026).",
    confidence: "high",
    learnMoreUrl: CTR_URL,
    learnMoreLabel: CTR_LABEL,
  };
}

/** Rule 5 — entry does not end in a period (and does not end in a bare URL/DOI). */
function checkTerminalPeriod(entry: string): Finding | null {
  const trimmed = entry.trim();
  if (RE_ENDS_WITH_URL.test(trimmed) || RE_TERMINAL_PUNCT.test(trimmed)) {
    return null;
  }
  return {
    rule: "terminal-period",
    title: "Entry does not end with a period",
    message:
      "Every Harvard reference-list entry ends with a period — including online sources, where the period follows the closing bracket of the access date. This entry ends without terminal punctuation.",
    confidence: "high",
    learnMoreUrl: CTR_URL,
    learnMoreLabel: CTR_LABEL,
  };
}

/** Run all per-entry rules for a single normalized entry. */
function checkEntry(entry: string): Finding[] {
  const findings: Finding[] = [];
  const authorSeg = authorSegment(entry);
  if (authorSeg) {
    const initials = checkAuthorInitials(authorSeg);
    if (initials) {
      findings.push(initials);
    }
  }
  const year = checkYearInParens(entry);
  if (year) {
    findings.push(year);
  }
  const sentenceCase = checkSentenceCase(entry);
  if (sentenceCase) {
    findings.push(sentenceCase);
  }
  const pairing = checkAvailableAccessed(entry);
  if (pairing) {
    findings.push(pairing);
  }
  const period = checkTerminalPeriod(entry);
  if (period) {
    findings.push(period);
  }
  return findings;
}

/** First-author surname (or article-stripped start) used for ordering + duplicate keys. */
function sortKey(entry: string): string {
  const authorSeg = authorSegment(entry);
  let basis: string;
  if (authorSeg) {
    basis = authorSeg.includes(",")
      ? authorSeg.slice(0, authorSeg.indexOf(","))
      : (authorSeg.split(RE_WORD_SPLIT)[0] ?? authorSeg);
  } else {
    basis = entry.replace(RE_LEADING_ARTICLE, "");
  }
  return basis.toLowerCase().replace(RE_NON_LETTER, "");
}

/** Rule 6 — flag entries that break alphabetical (A–Z by surname) order. */
function checkOrdering(entries: string[]): Finding[] {
  const findings: Finding[] = [];
  const keys = entries.map(sortKey);
  for (let i = 1; i < keys.length; i++) {
    const prev = keys[i - 1];
    const cur = keys[i];
    if (prev && cur && prev.localeCompare(cur) > 0) {
      findings.push({
        rule: "list-ordering",
        title: `Entry ${i + 1} may be out of alphabetical order`,
        message: `Harvard reference lists are ordered A–Z by the first author's surname. Entry ${i + 1} sorts before entry ${i} ("${cur}" < "${prev}"). Check the ordering of these two entries.`,
        confidence: "high",
        learnMoreUrl: CTR_URL,
        learnMoreLabel: CTR_LABEL,
      });
    }
  }
  return findings;
}

/**
 * Rule 7 — the connector before the final author should be used CONSISTENTLY across the
 * list. Cite Them Right uses the word "and"; some institutional Harvard variants use "&".
 * Either is defensible, but mixing the two in one list is always an error, so this only
 * fires when both appear in author segments.
 */
function checkConnectorConsistency(entries: string[]): Finding | null {
  let usesAnd = false;
  let usesAmp = false;
  for (const entry of entries) {
    const seg = authorSegment(entry);
    if (!seg) {
      continue;
    }
    if (RE_AND_CONNECTOR.test(seg)) {
      usesAnd = true;
    }
    if (RE_AMPERSAND.test(seg)) {
      usesAmp = true;
    }
  }
  if (!(usesAnd && usesAmp)) {
    return null;
  }
  return {
    rule: "connector-consistency",
    title: 'List mixes "and" and "&" before the final author',
    message:
      'Some entries join the last two authors with the word "and" and others with "&". Cite Them Right uses "and" (some institutional Harvard variants use "&") — either way, pick one and use it consistently throughout the list.',
    confidence: "high",
    learnMoreUrl: CTR_URL,
    learnMoreLabel: CTR_LABEL,
  };
}

/** Near-duplicate identity: surname + year (if any) + first significant title words. */
function duplicateKey(entry: string): string {
  const surname = sortKey(entry);
  const yearMatch = RE_YEAR_ANYWHERE.exec(entry);
  const year = yearMatch ? yearMatch[1] : "";
  const title = titleSegment(entry) ?? "";
  const titleWord = title
    .trim()
    .split(RE_WORD_SPLIT)
    .slice(0, 4)
    .join("")
    .toLowerCase()
    .replace(RE_PUNCT_STRIP, "");
  return `${surname}|${year}|${titleWord}`;
}

/** Rule 8 — flag near-duplicate entries (same surname + year + title start). */
function checkDuplicates(entries: string[]): Finding[] {
  const findings: Finding[] = [];
  const seen = new Map<string, number>();
  for (let i = 0; i < entries.length; i++) {
    const key = duplicateKey(entries[i]);
    if (!key || key.replace(RE_PIPES, "") === "") {
      continue;
    }
    const firstIndex = seen.get(key);
    if (firstIndex === undefined) {
      seen.set(key, i);
      continue;
    }
    findings.push({
      rule: "duplicate-entry",
      title: `Entry ${i + 1} may duplicate entry ${firstIndex + 1}`,
      message: `Entries ${firstIndex + 1} and ${i + 1} share the same author, year, and title start. A reference list should cite each source once — repeat citations of the same work with the same year need a/b/c letters ("2020a", "2020b") instead.`,
      confidence: "high",
      learnMoreUrl: CTR_URL,
      learnMoreLabel: CTR_LABEL,
    });
  }
  return findings;
}

/**
 * Run the full deterministic Harvard (Cite Them Right) reference-list check over pasted
 * text. Pure — no network, no mutation, no dependency on the citation core.
 */
export function checkReferenceList(text: string): CheckReport {
  const rawEntries = splitEntries(text);
  const entries: EntryReport[] = rawEntries.map((entry, index) => ({
    position: index + 1,
    text: entry,
    findings: checkEntry(entry),
  }));
  const connector = checkConnectorConsistency(rawEntries);
  const listFindings = [
    ...checkOrdering(rawEntries),
    ...(connector ? [connector] : []),
    ...checkDuplicates(rawEntries),
  ];
  const totalFindings =
    listFindings.length +
    entries.reduce((sum, e) => sum + e.findings.length, 0);
  return {
    entries,
    listFindings,
    totalEntries: entries.length,
    totalFindings,
  };
}
